// state/slice/verificationSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getVerificationThunk,
  submitVerificationThunk,
  getVerificationLogsThunk,
  assignVerificatorThunk,
  resetVerificationStageThunk,
  generateInvoiceThunk,
} from "state/action/verification";

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * VERIFICATION_STAGES — urutan dan definisi setiap stage verifikasi.
 *
 * Alur Hybrid:
 *   Stage 1 (pks)                   → SEQUENTIAL (harus selesai dulu)
 *   Stage 2 (revenue_recognition)   → PARALLEL setelah stage 1 selesai
 *   Stage 3 (cash_flow)             → PARALLEL setelah stage 1 selesai
 *
 * PKS berstatus "Active" hanya jika ketiga stage sudah "approved".
 */
export const VERIFICATION_STAGES = {
  PKS: "pks",
  REVENUE_RECOGNITION: "revenue_recognition",
  CASH_FLOW: "cash_flow",
};

export const VERIFICATION_ACTIONS = {
  APPROVE: "approved",
  REJECT: "rejected",
  REQUEST_REVISION: "request_revision",
};

export const STAGE_STATUS = {
  PENDING: "pending", // Belum ada tindakan
  IN_REVIEW: "in_review", // Sedang dalam review
  APPROVED: "approved", // Disetujui
  REJECTED: "rejected", // Ditolak
  REVISION: "revision", // Diminta revisi
  LOCKED: "locked", // Terkunci (stage sebelumnya belum selesai)
};

/**
 * Konfigurasi tiap stage: label, unit kerja, dan role yang berwenang.
 */
export const STAGE_CONFIG = {
  [VERIFICATION_STAGES.PKS]: {
    label: "Verifikasi PKS",
    unit_kerja: "Unit Kerja Terkait Layanan",
    description:
      "Dilakukan oleh unit kerja yang terdaftar pada tipe layanan PKS.",
    color: "primary",
    order: 1,
    is_parallel: false,
  },
  [VERIFICATION_STAGES.REVENUE_RECOGNITION]: {
    label: "Verifikasi Revenue Recognition",
    unit_kerja: "Treasury and Tax Department (TTD)",
    description:
      "Dilakukan oleh Treasury and Tax Department setelah PKS terverifikasi.",
    color: "info",
    order: 2,
    is_parallel: true,
  },
  [VERIFICATION_STAGES.CASH_FLOW]: {
    label: "Verifikasi Cash Flow",
    unit_kerja: "Accounting and Financial Control Department (AFCD)",
    description:
      "Dilakukan oleh Accounting and Financial Control Departement setelah PKS terverifikasi.",
    color: "success",
    order: 3,
    is_parallel: true,
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const asyncState = () => ({
  data: null,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
});

/**
 * Hitung status keseluruhan PKS berdasarkan semua stage.
 * @param {object} stages — { pks, revenue_recognition, cash_flow }
 * @returns {"draft"|"in_review"|"partially_verified"|"active"|"rejected"}
 */
export function computeOverallStatus(stages) {
  if (!stages) return "draft";
  const pks = stages[VERIFICATION_STAGES.PKS]?.status;
  const rev = stages[VERIFICATION_STAGES.REVENUE_RECOGNITION]?.status;
  const cf = stages[VERIFICATION_STAGES.CASH_FLOW]?.status;

  if ([pks, rev, cf].some((s) => s === STAGE_STATUS.REJECTED))
    return "rejected";
  if (pks !== STAGE_STATUS.APPROVED) return "in_review";
  if (rev === STAGE_STATUS.APPROVED && cf === STAGE_STATUS.APPROVED)
    return "active";
  return "partially_verified";
}

/**
 * Tentukan apakah user dapat melakukan aksi di stage tertentu.
 *
 * Otorisasi kini berbasis work_unit dari service_type PKS, bukan role string.
 *
 * @param {string}   stage            — stage yang diperiksa
 * @param {object}   stages           — semua stage data
 * @param {string}   userWorkUnitId   — _id primaryWorkUnit user yang login
 * @param {string[]} serviceWorkUnits — kumpulan work_unit._id dari semua service_type PKS
 * @returns {boolean}
 */
export function canActOnStage(
  stage,
  stages,
  userWorkUnitId,
  serviceWorkUnits = [],
) {
  if (!userWorkUnitId) return false;

  let isAuthorized = false;

  if (stage === VERIFICATION_STAGES.PKS) {
    // Stage PKS: Diotorisasi oleh unit kerja pada layanan
    const workUnitIds = serviceWorkUnits.map((wu) =>
      typeof wu === "object" ? (wu._id ?? wu) : wu,
    );
    isAuthorized = workUnitIds.some(
      (id) => String(id) === String(userWorkUnitId),
    );
  } else if (stage === VERIFICATION_STAGES.REVENUE_RECOGNITION) {
    // Stage Rev. Rec: Hanya TTD
    isAuthorized = String(userWorkUnitId) === "69d1e2efd2125463a66a6570";
  } else if (stage === VERIFICATION_STAGES.CASH_FLOW) {
    // Stage Cash Flow: Hanya AFCD
    isAuthorized = String(userWorkUnitId) === "69d1e326d2125463a66a65b5";
  }

  if (!isAuthorized) return false;

  const stageData = stages?.[stage];
  if (!stageData) return false;

  // Stage PKS tidak butuh prasyarat
  if (stage === VERIFICATION_STAGES.PKS) {
    return [
      STAGE_STATUS.PENDING,
      STAGE_STATUS.IN_REVIEW,
      STAGE_STATUS.REVISION,
    ].includes(stageData.status);
  }

  // Stage Revenue & Cash Flow butuh PKS sudah approved
  const pksApproved =
    stages?.[VERIFICATION_STAGES.PKS]?.status === STAGE_STATUS.APPROVED;
  if (!pksApproved) return false;

  return [
    STAGE_STATUS.PENDING,
    STAGE_STATUS.IN_REVIEW,
    STAGE_STATUS.REVISION,
  ].includes(stageData.status);
}

// ─── Initial State ────────────────────────────────────────────────────────────
const initialStageData = (status = STAGE_STATUS.PENDING) => ({
  status,
  verified_by: null,
  verified_at: null,
  action: null,
  comment: "",
  item_comments: [],
  history: [],
});

const initialState = {
  // Async states
  getVerification: asyncState(),
  submitVerification: asyncState(),
  getLogs: asyncState(),
  assignVerificator: asyncState(),
  resetStage: asyncState(),
  generateInvoice: asyncState(),

  // Data verifikasi dari API
  verificationData: null,

  // Stages (mirror dari API, bisa di-override lokal untuk optimistic update)
  stages: {
    [VERIFICATION_STAGES.PKS]: initialStageData(STAGE_STATUS.PENDING),
    [VERIFICATION_STAGES.REVENUE_RECOGNITION]: initialStageData(
      STAGE_STATUS.LOCKED,
    ),
    [VERIFICATION_STAGES.CASH_FLOW]: initialStageData(STAGE_STATUS.LOCKED),
  },

  // Overall status PKS
  overallStatus: "draft",

  // Log verifikasi
  logs: [],

  // Invoice generation flag
  invoice_generated: false,

  // UI state — form aksi verifikasi yang sedang dibuka
  activeActionForm: {
    isOpen: false,
    stage: null,
    action: null, // "approved" | "rejected" | "request_revision"
    comment: "",
    item_comments: [], // [{ item_key, comment }]
  },

  // UI — stage yang sedang dilihat detailnya
  selectedStage: null,

  // PKS ID yang sedang diverifikasi
  pksId: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────
export const verificationSlice = createSlice({
  name: "verification",
  initialState,

  reducers: {
    // ── Set PKS ID ────────────────────────────────────────────────────────────
    setPksId: (state, action) => {
      state.pksId = action.payload;
    },

    // ── Buka form aksi verifikasi ─────────────────────────────────────────────
    openActionForm: (state, action) => {
      const { stage, actionType } = action.payload;
      state.activeActionForm = {
        isOpen: true,
        stage,
        action: actionType,
        comment: "",
        item_comments: [],
      };
    },

    // ── Tutup form aksi verifikasi ────────────────────────────────────────────
    closeActionForm: (state) => {
      state.activeActionForm = initialState.activeActionForm;
    },

    // ── Update comment utama di form ──────────────────────────────────────────
    setActionComment: (state, action) => {
      state.activeActionForm.comment = action.payload;
    },

    // ── Tambah / update komentar per item ─────────────────────────────────────
    setItemComment: (state, action) => {
      const { item_key, comment } = action.payload;
      const existing = state.activeActionForm.item_comments.findIndex(
        (ic) => ic.item_key === item_key,
      );
      if (existing >= 0) {
        state.activeActionForm.item_comments[existing].comment = comment;
      } else {
        state.activeActionForm.item_comments.push({ item_key, comment });
      }
      // Hapus jika comment dikosongkan
      if (!comment) {
        state.activeActionForm.item_comments =
          state.activeActionForm.item_comments.filter(
            (ic) => ic.item_key !== item_key,
          );
      }
    },

    // ── Hapus item comment ────────────────────────────────────────────────────
    removeItemComment: (state, action) => {
      state.activeActionForm.item_comments =
        state.activeActionForm.item_comments.filter(
          (ic) => ic.item_key !== action.payload,
        );
    },

    // ── Set action type di form (bisa ganti dari approve ke reject dll) ────────
    setActionType: (state, action) => {
      state.activeActionForm.action = action.payload;
      state.activeActionForm.comment = "";
      state.activeActionForm.item_comments = [];
    },

    // ── Set stage yang dipilih untuk lihat detail ─────────────────────────────
    setSelectedStage: (state, action) => {
      state.selectedStage = action.payload;
    },

    // ── Patch stage lokal (optimistic update / mock) ──────────────────────────
    patchStage: (state, action) => {
      const { stage, data } = action.payload;
      if (state.stages[stage]) {
        state.stages[stage] = { ...state.stages[stage], ...data };
        // Recalculate locked status untuk stage parallel
        const pksApproved =
          state.stages[VERIFICATION_STAGES.PKS]?.status ===
          STAGE_STATUS.APPROVED;
        if (pksApproved) {
          [
            VERIFICATION_STAGES.REVENUE_RECOGNITION,
            VERIFICATION_STAGES.CASH_FLOW,
          ].forEach((s) => {
            if (state.stages[s]?.status === STAGE_STATUS.LOCKED) {
              state.stages[s].status = STAGE_STATUS.PENDING;
            }
          });
        }
        state.overallStatus = computeOverallStatus(state.stages);
      }
    },

    // ── Clear individual async state ──────────────────────────────────────────
    setClearGetVerification: (state) => ({
      ...state,
      getVerification: asyncState(),
    }),
    setClearSubmitVerification: (state) => ({
      ...state,
      submitVerification: asyncState(),
    }),
    setClearGetLogs: (state) => ({ ...state, getLogs: asyncState() }),

    // ── Reset semua state ─────────────────────────────────────────────────────
    setClearAllVerificationState: () => ({ ...initialState }),
  },

  // ─── Extra Reducers ─────────────────────────────────────────────────────────
  extraReducers(builder) {
    builder
      // GET VERIFICATION
      .addCase(getVerificationThunk.pending, (state) => ({
        ...state,
        getVerification: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getVerificationThunk.fulfilled, (state, action) => {
        const payload = action.payload;
        const stages = payload?.data?.stages || payload?.stages || {};

        // Unlock parallel stages jika PKS sudah approved
        const pksApproved =
          stages[VERIFICATION_STAGES.PKS]?.status === STAGE_STATUS.APPROVED;
        if (!pksApproved) {
          [
            VERIFICATION_STAGES.REVENUE_RECOGNITION,
            VERIFICATION_STAGES.CASH_FLOW,
          ].forEach((s) => {
            if (
              !stages[s]?.status ||
              stages[s].status === STAGE_STATUS.PENDING
            ) {
              stages[s] = { ...(stages[s] || {}), status: STAGE_STATUS.LOCKED };
            }
          });
        }

        return {
          ...state,
          getVerification: {
            data: payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
          verificationData: payload?.data || payload,
          stages: { ...state.stages, ...stages },
          overallStatus: computeOverallStatus(stages),
        };
      })
      .addCase(getVerificationThunk.rejected, (state, action) => ({
        ...state,
        getVerification: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // SUBMIT VERIFICATION
      .addCase(submitVerificationThunk.pending, (state) => ({
        ...state,
        submitVerification: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(submitVerificationThunk.fulfilled, (state, action) => {
        const payload = action.payload;
        // Tangani jika response dibungkus dalam properti "data"
        const responseData = payload?.data || payload;
        const stage = responseData?.stage;
        const updatedStages = { ...state.stages };

        if (stage && responseData?.stage_data) {
          updatedStages[stage] = {
            ...updatedStages[stage],
            ...responseData.stage_data,
          };

          // Unlock parallel stages jika PKS baru saja diapprove
          if (
            stage === VERIFICATION_STAGES.PKS &&
            responseData.stage_data.status === STAGE_STATUS.APPROVED
          ) {
            [
              VERIFICATION_STAGES.REVENUE_RECOGNITION,
              VERIFICATION_STAGES.CASH_FLOW,
            ].forEach((s) => {
              if (updatedStages[s]?.status === STAGE_STATUS.LOCKED) {
                updatedStages[s] = {
                  ...updatedStages[s],
                  status: STAGE_STATUS.PENDING,
                };
              }
            });
          }
        }

        return {
          ...state,
          submitVerification: {
            data: payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
          stages: updatedStages,
          overallStatus: computeOverallStatus(updatedStages),
          activeActionForm: initialState.activeActionForm,
        };
      })
      .addCase(submitVerificationThunk.rejected, (state, action) => ({
        ...state,
        submitVerification: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // GET LOGS
      .addCase(getVerificationLogsThunk.pending, (state) => ({
        ...state,
        getLogs: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getVerificationLogsThunk.fulfilled, (state, action) => ({
        ...state,
        getLogs: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
        logs: action.payload?.logs || [],
      }))
      .addCase(getVerificationLogsThunk.rejected, (state, action) => ({
        ...state,
        getLogs: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // ASSIGN VERIFICATOR
      .addCase(assignVerificatorThunk.pending, (state) => ({
        ...state,
        assignVerificator: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(assignVerificatorThunk.fulfilled, (state, action) => ({
        ...state,
        assignVerificator: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(assignVerificatorThunk.rejected, (state, action) => ({
        ...state,
        assignVerificator: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // RESET STAGE
      .addCase(resetVerificationStageThunk.pending, (state) => ({
        ...state,
        resetStage: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(resetVerificationStageThunk.fulfilled, (state, action) => ({
        ...state,
        resetStage: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(resetVerificationStageThunk.rejected, (state, action) => ({
        ...state,
        resetStage: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // GENERATE INVOICE
      .addCase(generateInvoiceThunk.pending, (state) => ({
        ...state,
        generateInvoice: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(generateInvoiceThunk.fulfilled, (state, action) => ({
        ...state,
        generateInvoice: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
        // Mark that invoice has been generated
        invoice_generated: true,
      }))
      .addCase(generateInvoiceThunk.rejected, (state, action) => ({
        ...state,
        generateInvoice: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }));
  },
});

// ─── Export actions ───────────────────────────────────────────────────────────
export const verificationAction = {
  ...verificationSlice.actions,
  getVerificationThunk,
  submitVerificationThunk,
  getVerificationLogsThunk,
  assignVerificatorThunk,
  resetVerificationStageThunk,
  generateInvoiceThunk,
};

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectVerification = (s) => s?.verification;
export const selectStages = (s) => s?.verification?.stages;
export const selectStage = (stage) => (s) => s?.verification?.stages?.[stage];
export const selectOverallStatus = (s) => s?.verification?.overallStatus;
export const selectActiveActionForm = (s) => s?.verification?.activeActionForm;
export const selectSelectedStage = (s) => s?.verification?.selectedStage;
export const selectVerificationLogs = (s) => s?.verification?.logs;
export const selectGetVerification = (s) => s?.verification?.getVerification;
export const selectSubmitVerification = (s) =>
  s?.verification?.submitVerification;
export const selectPksId = (s) => s?.verification?.pksId;

export default verificationSlice.reducer;
